import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Trash2, Edit, Check, Users, RefreshCw,
  Calendar, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import PageLayout from '../components/PageLayout';

// ── Assignment card ───────────────────────────────────────────
const AssignmentRow = ({ task, users, onComplete, onDelete, onEdit }) => {
  const participant = users.find(u => u.user_id === task.assigned_to);
  const name = participant?.name || task.assigned_to || '—';
  const overdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  return (
    <div className={`flex items-start justify-between gap-3 p-4 rounded-xl border transition-colors
      ${task.status === 'completed' ? 'bg-slate-50 border-slate-100 opacity-60'
        : overdue ? 'bg-red-50 border-red-200'
        : 'bg-amber-50 border-amber-200'}`}
      data-testid={`assignment-${task.task_id}`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <button
          onClick={() => task.status !== 'completed' && onComplete(task.task_id)}
          disabled={task.status === 'completed'}
          className="mt-0.5 flex-shrink-0"
        >
          {task.status === 'completed'
            ? <Check className="w-5 h-5 text-green-500" />
            : <div className="w-5 h-5 rounded border-2 border-amber-400 hover:border-amber-600 transition-colors" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className={`text-sm font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-800'}`}>
              {task.title}
            </p>
            {task.is_perpetual && (
              <Badge className="bg-blue-100 text-blue-700 border-0 text-xs flex items-center gap-1">
                <RefreshCw className="w-2.5 h-2.5" />Perpetual
              </Badge>
            )}
            {overdue && <Badge className="bg-red-100 text-red-700 border-0 text-xs">Overdue</Badge>}
          </div>
          {task.description && <p className="text-xs text-slate-500 mt-0.5">{task.description}</p>}
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            <span className="text-xs text-slate-500 flex items-center gap-1">
              <Users className="w-3 h-3" />{name}
            </span>
            {task.trip_name && <span className="text-xs text-slate-400">{task.trip_name}</span>}
            {task.due_date && (
              <span className={`text-xs flex items-center gap-1 ${overdue ? 'text-red-600' : 'text-slate-400'}`}>
                <Calendar className="w-3 h-3" />Due {task.due_date}
              </span>
            )}
          </div>
          {task.completion_notes && <p className="text-xs text-green-600 mt-1 italic">Note: {task.completion_notes}</p>}
        </div>
      </div>
      <div className="flex flex-col gap-1 flex-shrink-0">
        {task.status !== 'completed' && (
          <button onClick={() => onEdit(task)} className="text-slate-300 hover:text-primary transition-colors" title="Edit" data-testid={`edit-assignment-${task.task_id}`}>
            <Edit className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => onDelete(task.task_id)} className="text-slate-300 hover:text-red-500 transition-colors" title="Delete">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
const AssignmentsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTrip, setFilterTrip] = useState('all');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [search, setSearch] = useState('');

  // New assignment dialog
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({
    title: '', description: '', assigned_to: '', trip_id: '', due_date: '',
    is_perpetual: false, template_id: ''
  });
  const [assigning, setAssigning] = useState(false);

  // Edit assignment dialog
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', assigned_to: '', trip_id: '', due_date: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Template dialog
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [tplForm, setTplForm] = useState({ title: '', description: '', is_perpetual: false });
  const [savingTpl, setSavingTpl] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [tplRes, taskRes, userRes, tripRes] = await Promise.all([
        api.get('/assignment-templates'),
        api.get('/tasks'),
        api.get('/users'),
        api.get('/trips'),
      ]);
      setTemplates(tplRes.data);
      // Enrich tasks with trip/user names
      const tripMap = Object.fromEntries(tripRes.data.map(t => [t.trip_id, t.name]));
      setTasks(tplRes.data && taskRes.data.map(t => ({ ...t, trip_name: tripMap[t.trip_id] || '' })));
      setUsers(userRes.data);
      setTrips(tripRes.data);
      // Fix: use taskRes not tplRes for tasks
      const enriched = taskRes.data.map(t => ({ ...t, trip_name: tripMap[t.trip_id] || '' }));
      setTasks(enriched);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Open assign dialog, optionally pre-fill from template
  const openAssign = (template = null) => {
    setAssignForm({
      title: template?.title || '',
      description: template?.description || '',
      is_perpetual: template?.is_perpetual || false,
      template_id: template?.template_id || '',
      assigned_to: '', trip_id: '', due_date: ''
    });
    setShowAssignDialog(true);
  };

  const handleAssign = async () => {
    if (!assignForm.title || !assignForm.assigned_to) {
      toast.error('Title and participant are required');
      return;
    }
    setAssigning(true);
    try {
      await api.post('/tasks', {
        title: assignForm.title,
        description: assignForm.description || undefined,
        assigned_to: assignForm.assigned_to,
        trip_id: assignForm.trip_id && assignForm.trip_id !== 'none' ? assignForm.trip_id : undefined,
        due_date: assignForm.due_date || undefined,
        is_perpetual: assignForm.is_perpetual,
        template_id: assignForm.template_id || undefined,
        task_type: 'assignment',
      });
      toast.success('Assignment created — participant will see it on their Dashboard');
      setShowAssignDialog(false);
      fetchAll();
    } catch { toast.error('Failed to create'); }
    finally { setAssigning(false); }
  };

  const handleComplete = async (taskId) => {
    try {
      await api.put(`/tasks/${taskId}`, { status: 'completed' });
      toast.success('Marked complete');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Delete this assignment?')) return;
    try {
      await api.delete(`/tasks/${taskId}`).catch(() => api.put(`/tasks/${taskId}`, { status: 'deleted' }));
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleEditTask = (task) => {
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      assigned_to: task.assigned_to || '',
      trip_id: task.trip_id || '',
      due_date: task.due_date || '',
    });
    setEditingTask(task);
  };

  const handleSaveEdit = async () => {
    if (!editForm.title.trim()) { toast.error('Title required'); return; }
    setSavingEdit(true);
    try {
      await api.put(`/tasks/${editingTask.task_id}`, editForm);
      toast.success('Assignment updated');
      setEditingTask(null);
      fetchAll();
    } catch { toast.error('Failed to update'); }
    finally { setSavingEdit(false); }
  };


  const handleSaveTemplate = async () => {
    if (!tplForm.title.trim()) { toast.error('Title required'); return; }
    setSavingTpl(true);
    try {
      if (editingTemplate) {
        await api.put(`/assignment-templates/${editingTemplate.template_id}`, tplForm);
        toast.success('Template updated');
      } else {
        await api.post('/assignment-templates', tplForm);
        toast.success('Template added to library');
      }
      setShowTemplateDialog(false);
      setEditingTemplate(null);
      setTplForm({ title: '', description: '', is_perpetual: false });
      fetchAll();
    } catch { toast.error('Failed to save'); }
    finally { setSavingTpl(false); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm('Delete this template from the library?')) return;
    try {
      await api.delete(`/assignment-templates/${id}`);
      toast.success('Template removed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  // Filter tasks
  const filtered = tasks.filter(t => {
    if (filterStatus === 'pending' && t.status === 'completed') return false;
    if (filterStatus === 'completed' && t.status !== 'completed') return false;
    if (filterTrip !== 'all' && t.trip_id !== filterTrip) return false;
    if (search) {
      const term = search.toLowerCase();
      const name = users.find(u => u.user_id === t.assigned_to)?.name?.toLowerCase() || '';
      if (!t.title.toLowerCase().includes(term) && !name.includes(term)) return false;
    }
    return true;
  });

  const pendingCount = tasks.filter(t => t.status !== 'completed').length;

  const assignActions = (
    <>
      {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200">{pendingCount} pending</Badge>}
      <Button variant="outline" size="sm" className="rounded-full h-8 text-xs"
        onClick={() => { setEditingTemplate(null); setTplForm({ title: '', description: '', is_perpetual: false }); setShowTemplateDialog(true); }}
        data-testid="manage-library-btn">
        <ClipboardList className="w-3.5 h-3.5 mr-1.5" />Library
      </Button>
      <Button size="sm" className="rounded-full bg-primary h-8"
        onClick={() => openAssign()} data-testid="new-assignment-btn">
        <Plus className="w-3.5 h-3.5 mr-1.5" />New Assignment
      </Button>
    </>
  );

  return (
    <PageLayout title="Assignments" headerActions={assignActions} maxWidth="max-w-5xl">
          <Tabs defaultValue="active">
            <TabsList className="mb-5">
              <TabsTrigger value="active" data-testid="tab-active">
                Active
                {pendingCount > 0 && <Badge className="ml-2 bg-amber-500 text-white border-0 text-xs">{pendingCount}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">
                Completed ({tasks.filter(t => t.status === 'completed').length})
              </TabsTrigger>
              <TabsTrigger value="library" data-testid="tab-library">
                Library ({templates.length})
              </TabsTrigger>
            </TabsList>

            {/* ── Active + Completed tabs share filter UI ── */}
            {['active', 'completed'].map(tabVal => (
              <TabsContent key={tabVal} value={tabVal}>
                <div className="flex flex-wrap gap-2 mb-4">
                  <div className="relative flex-1 min-w-36">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                    <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-sm" />
                  </div>
                  <Select value={filterTrip} onValueChange={setFilterTrip}>
                    <SelectTrigger className="h-8 text-sm w-44"><SelectValue placeholder="All Trips" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Trips</SelectItem>
                      {trips.map(t => <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>)}
                      <SelectItem value="none">No trip (general)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {loading ? (
                  <div className="text-center py-12 text-slate-400">Loading...</div>
                ) : (() => {
                    const tabTasks = tasks.filter(t =>
                      tabVal === 'active' ? t.status !== 'completed' : t.status === 'completed'
                    ).filter(t => {
                      if (filterTrip !== 'all' && t.trip_id !== filterTrip) return false;
                      if (search) {
                        const term = search.toLowerCase();
                        const name = users.find(u => u.user_id === t.assigned_to)?.name?.toLowerCase() || '';
                        if (!t.title.toLowerCase().includes(term) && !name.includes(term)) return false;
                      }
                      return true;
                    });
                    return tabTasks.length === 0 ? (
                      <div className="text-center py-12">
                        <ClipboardList className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                        <p className="text-slate-400 text-sm">
                          {tabVal === 'active' ? 'No active assignments' : 'No completed assignments'}
                        </p>
                        {tabVal === 'active' && (
                          <Button size="sm" className="mt-4 rounded-full bg-primary" onClick={() => openAssign()}>
                            <Plus className="w-3.5 h-3.5 mr-1.5" />Create First Assignment
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {tabTasks.map(t => (
                          <AssignmentRow key={t.task_id} task={t} users={users}
                            onComplete={handleComplete} onDelete={handleDelete} onEdit={handleEditTask} />
                        ))}
                      </div>
                    );
                  })()}
              </TabsContent>
            ))}

            {/* ── Library tab ── */}
            <TabsContent value="library">
              {templates.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardList className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm mb-4">No templates yet — add reusable assignments to the library</p>
                  <Button size="sm" className="rounded-full bg-primary"
                    onClick={() => { setEditingTemplate(null); setTplForm({ title: '', description: '', is_perpetual: false }); setShowTemplateDialog(true); }}>
                    <Plus className="w-3.5 h-3.5 mr-1.5" />Add to Library
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {templates.map(tpl => (
                    <Card key={tpl.template_id} data-testid={`template-${tpl.template_id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-slate-800 text-sm">{tpl.title}</p>
                              {tpl.is_perpetual && (
                                <Badge className="bg-blue-100 text-blue-700 border-0 text-xs flex items-center gap-1">
                                  <RefreshCw className="w-2.5 h-2.5" />Perpetual — resets 21 days after trip
                                </Badge>
                              )}
                            </div>
                            {tpl.description && <p className="text-xs text-slate-500 mt-0.5">{tpl.description}</p>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Button size="sm" className="h-7 text-xs rounded-full bg-primary"
                              onClick={() => openAssign(tpl)} data-testid={`assign-tpl-${tpl.template_id}`}>
                              <Plus className="w-3 h-3 mr-1" />Assign
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-primary"
                              onClick={() => { setEditingTemplate(tpl); setTplForm({ title: tpl.title, description: tpl.description || '', is_perpetual: tpl.is_perpetual }); setShowTemplateDialog(true); }}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-300 hover:text-red-500"
                              onClick={() => handleDeleteTemplate(tpl.template_id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

      {/* ── New Assignment Dialog ── */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {assignForm.template_id ? `Assign: ${assignForm.title}` : 'New Assignment'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {!assignForm.template_id && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Title *</label>
                <Input value={assignForm.title} onChange={e => setAssignForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g., Apply for Equipment Grant" data-testid="assign-title-input" />
              </div>
            )}
            {!assignForm.template_id && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Description (optional)</label>
                <Textarea value={assignForm.description} onChange={e => setAssignForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Details or instructions..." rows={2} className="resize-none text-sm" />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Assign To *</label>
              <Select value={assignForm.assigned_to} onValueChange={v => setAssignForm(f => ({ ...f, assigned_to: v }))}>
                <SelectTrigger data-testid="assign-to-select"><SelectValue placeholder="Select participant..." /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Trip (optional)</label>
                <Select value={assignForm.trip_id} onValueChange={v => setAssignForm(f => ({ ...f, trip_id: v }))}>
                  <SelectTrigger className="text-sm"><SelectValue placeholder="Any trip" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific trip</SelectItem>
                    {trips.map(t => <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Due Date (optional)</label>
                <Input type="date" value={assignForm.due_date} onChange={e => setAssignForm(f => ({ ...f, due_date: e.target.value }))}
                  className="text-sm" data-testid="assign-due-input" />
              </div>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Switch checked={assignForm.is_perpetual} onCheckedChange={v => setAssignForm(f => ({ ...f, is_perpetual: v }))} />
              <div>
                <p className="text-sm text-slate-700">Perpetual</p>
                <p className="text-xs text-slate-400">Resets 21 days after trip ends — re-assign each year</p>
              </div>
            </div>
            {!assignForm.template_id && (
              <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
                <input type="checkbox" id="save-to-lib" className="accent-primary"
                  onChange={async (e) => {
                    if (e.target.checked && assignForm.title) {
                      await api.post('/assignment-templates', {
                        title: assignForm.title,
                        description: assignForm.description,
                        is_perpetual: assignForm.is_perpetual
                      });
                      toast.success('Saved to library for future use');
                    }
                  }} />
                <label htmlFor="save-to-lib" className="text-xs text-slate-500 cursor-pointer">
                  Save to library for future reuse
                </label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>Cancel</Button>
            <Button className="bg-primary" onClick={handleAssign} disabled={assigning || !assignForm.title || !assignForm.assigned_to}
              data-testid="confirm-assign-btn">
              {assigning ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Template Dialog ── */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Add to Library'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Title *</label>
              <Input value={tplForm.title} onChange={e => setTplForm(f => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Apply for Equipment Grant" data-testid="tpl-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Description (optional)</label>
              <Textarea value={tplForm.description} onChange={e => setTplForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Instructions or context for this assignment..." rows={3} className="resize-none text-sm" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={tplForm.is_perpetual} onCheckedChange={v => setTplForm(f => ({ ...f, is_perpetual: v }))} />
              <div>
                <p className="text-sm text-slate-700">Perpetual</p>
                <p className="text-xs text-slate-400">When assigned, resets 21 days after each trip ends</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateDialog(false)}>Cancel</Button>
            <Button className="bg-primary" onClick={handleSaveTemplate} disabled={savingTpl} data-testid="save-tpl-btn">
              {savingTpl ? 'Saving...' : editingTemplate ? 'Save Changes' : 'Add to Library'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Assignment Dialog ── */}
      <Dialog open={!!editingTask} onOpenChange={(open) => !open && setEditingTask(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Assignment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Title *</label>
              <Input value={editForm.title} onChange={(e) => setEditForm(f => ({ ...f, title: e.target.value }))} data-testid="edit-assignment-title" />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Description</label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm(f => ({ ...f, description: e.target.value }))} rows={3} data-testid="edit-assignment-desc" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Assign To</label>
                <Select value={editForm.assigned_to} onValueChange={(v) => setEditForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger data-testid="edit-assignment-user"><SelectValue placeholder="Select participant..." /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Trip</label>
                <Select value={editForm.trip_id || 'none'} onValueChange={(v) => setEditForm(f => ({ ...f, trip_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="edit-assignment-trip"><SelectValue placeholder="Select trip..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific trip</SelectItem>
                    {trips.map(t => <SelectItem key={t.trip_id} value={t.trip_id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Due Date</label>
              <Input type="date" value={editForm.due_date} onChange={(e) => setEditForm(f => ({ ...f, due_date: e.target.value }))} data-testid="edit-assignment-due" />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setEditingTask(null)}>Cancel</Button>
            <Button className="rounded-full bg-rojo hover:bg-rojo-dark" disabled={savingEdit || !editForm.title.trim()} onClick={handleSaveEdit} data-testid="save-edit-assignment-btn">
              {savingEdit ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </PageLayout>
  );
};

export default AssignmentsPage;
